import type { CookieConsentConfig, CookieValue } from "vanilla-cookieconsent";
import {
  GA_EXPIRES_TIME_DAYS,
  initGA,
} from "../../shared/utils/google-analytics";
import i18n from "../../shared/locales/i18n";

export enum CookiesCategories {
  necessary = "necessary",
  analytics = "analytics",
}

const applyCookies = (cookie: CookieValue) => {
  if (cookie.categories.includes(CookiesCategories.analytics)) {
    initGA();
  }
};

const getTranslation = (
  key: string,
  lang: string,
  fallback: string
): string => {
  try {
    // Get translation for the specific language
    const translation = i18n.getFixedT(lang)(key);
    return translation || fallback;
  } catch {
    return fallback;
  }
};

const getCookieConsentTranslations = (lang: string) => {
  const t = (key: string, fallback: string) =>
    getTranslation(`cookieConsent.${key}`, lang, fallback);

  return {
    consentModal: {
      title: t(
        "consentModal.title",
        lang === "pl"
          ? "Witaj miłośniku sushi, czas na ciasteczka!"
          : "Hello sushi seeker, it's cookie time!"
      ),
      description: t(
        "consentModal.description",
        lang === "pl"
          ? 'Nasza strona używa plików cookie do śledzenia, aby zrozumieć, jak z niej korzystasz. Śledzenie będzie włączone tylko wtedy, gdy wyrazisz na to zgodę. <a href="/docs/polityka_prywatnosci_daigo.pdf" data-cc="show-preferencesModal" class="cc__link"  target="_blank">Zarządzaj ustawieniami</a>'
          : 'Our website uses tracking cookies to understand how you interact with it. The tracking will be enabled only if you accept explicitly. <a href="/docs/polityka_prywatnosci_daigo.pdf" data-cc="show-preferencesModal" class="cc__link"  target="_blank">Manage preferences</a>'
      ),
      acceptAllBtn: t(
        "consentModal.acceptAllBtn",
        lang === "pl" ? "Akceptuj wszystkie" : "Accept all"
      ),
      acceptNecessaryBtn: t(
        "consentModal.acceptNecessaryBtn",
        lang === "pl" ? "Odrzuć wszystkie" : "Reject all"
      ),
      showPreferencesBtn: t(
        "consentModal.showPreferencesBtn",
        lang === "pl" ? "Zarządzaj ustawieniami" : "Manage preferences"
      ),
      footer: t(
        "consentModal.footer",
        lang === "pl"
          ? '<a href="/docs/polityka_prywatnosci_daigo.pdf"  target="_blank">Polityka prywatności</a>'
          : '<a href="/docs/polityka_prywatnosci_daigo.pdf"  target="_blank">Privacy Policy</a>'
      ),
    },
    preferencesModal: {
      title: t(
        "preferencesModal.title",
        lang === "pl"
          ? "Preferencje dotyczące ciasteczek"
          : "Cookie preferences"
      ),
      acceptAllBtn: t(
        "preferencesModal.acceptAllBtn",
        lang === "pl" ? "Akceptuj wszystkie" : "Accept all"
      ),
      acceptNecessaryBtn: t(
        "preferencesModal.acceptNecessaryBtn",
        lang === "pl" ? "Odrzuć wszystkie" : "Reject all"
      ),
      savePreferencesBtn: t(
        "preferencesModal.savePreferencesBtn",
        lang === "pl" ? "Zapisz ustawienia" : "Save preferences"
      ),
      closeIconLabel: t(
        "preferencesModal.closeIconLabel",
        lang === "pl" ? "Zamknij" : "Close"
      ),
      sections: [
        {
          title: t(
            "preferencesModal.sections.usage.title",
            lang === "pl" ? "Użycie plików cookie" : "Cookie Usage"
          ),
          description: t(
            "preferencesModal.sections.usage.description",
            lang === "pl"
              ? 'Używamy plików cookie w celu zapewnienia podstawowych funkcjonalności strony oraz poprawy Twojego doświadczenia online. Możesz w każdej chwili wybrać, które kategorie akceptujesz. Więcej informacji o plikach cookie i danych wrażliwych znajdziesz w <a href="/docs/polityka_prywatnosci_daigo.pdf" class="cc__link"  target="_blank">polityce prywatności</a>.'
              : 'We use cookies to ensure the basic functionalities of the website and to enhance your online experience. You can choose for each category to opt-in/out whenever you want. For more details relative to cookies and other sensitive data, please read the full <a href="/docs/polityka_prywatnosci_daigo.pdf" class="cc__link"  target="_blank">privacy policy</a>.'
          ),
        },
        {
          title: t(
            "preferencesModal.sections.necessary.title",
            lang === "pl"
              ? "Ściśle niezbędne pliki cookie"
              : "Strictly necessary cookies"
          ),
          description: t(
            "preferencesModal.sections.necessary.description",
            lang === "pl"
              ? "Pliki cookie potrzebne do zapisania Twoich ustawień i preferencji strony."
              : "Cookies needed to save Your preferences and site settings."
          ),
          linkedCategory: "necessary",
        },
        {
          title: t(
            "preferencesModal.sections.analytics.title",
            lang === "pl"
              ? "Pliki cookie wydajności i analityki"
              : "Performance and Analytics cookies"
          ),
          linkedCategory: "analytics",
          cookieTable: {
            headers: {
              name: t(
                "preferencesModal.sections.analytics.tableHeaders.name",
                lang === "pl" ? "Nazwa" : "Name"
              ),
              domain: t(
                "preferencesModal.sections.analytics.tableHeaders.domain",
                lang === "pl" ? "Usługa" : "Service"
              ),
              description: t(
                "preferencesModal.sections.analytics.tableHeaders.description",
                lang === "pl" ? "Opis" : "Description"
              ),
              expiration: t(
                "preferencesModal.sections.analytics.tableHeaders.expiration",
                lang === "pl" ? "Wygaśnięcie" : "Expiration"
              ),
            },
            body: [
              {
                name: "_ga",
                domain: "Google Analytics",
                description: t(
                  "preferencesModal.sections.analytics.cookies._ga.description",
                  lang === "pl"
                    ? 'Plik cookie ustawiony przez <a href="https://developers.google.com/analytics"  target="_blank">Google Analytics</a>.'
                    : 'Cookie set by <a href="https://developers.google.com/analytics"  target="_blank">Google Analytics</a>.'
                ),
                expiration: t(
                  "preferencesModal.sections.analytics.cookies._ga.expiration",
                  lang === "pl"
                    ? `Wygasa po ${GA_EXPIRES_TIME_DAYS} dniach`
                    : `Expires after ${GA_EXPIRES_TIME_DAYS} days`
                ).replace("{{days}}", String(GA_EXPIRES_TIME_DAYS)),
              },
              {
                name: "_gid",
                domain: "Google Analytics",
                description: t(
                  "preferencesModal.sections.analytics.cookies._gid.description",
                  lang === "pl"
                    ? 'Plik cookie ustawiony przez <a href="https://developers.google.com/analytics"  target="_blank">Google Analytics</a>'
                    : 'Cookie set by <a href="https://developers.google.com/analytics"  target="_blank">Google Analytics</a>'
                ),
                expiration: t(
                  "preferencesModal.sections.analytics.cookies._gid.expiration",
                  lang === "pl" ? "Sesja" : "Session"
                ),
              },
            ],
          },
        },
      ],
    },
  };
};

const createPluginConfig = (
  defaultLang: string = "pl"
): CookieConsentConfig => {
  return {
    guiOptions: {
      consentModal: {
        layout: "box",
        position: "bottom right",
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: "box",
        position: "left",
        equalWeightButtons: true,
        flipButtons: false,
      },
    },

    onFirstConsent: function ({ cookie }) {
      applyCookies(cookie);
    },

    onConsent: function ({ cookie }) {
      applyCookies(cookie);
    },

    onChange: function ({ changedCategories, cookie }) {
      applyCookies(cookie);
    },

    categories: {
      [CookiesCategories.necessary]: {
        readOnly: true,
        enabled: true,
      },
      [CookiesCategories.analytics]: {
        autoClear: {
          cookies: [
            {
              name: /^(_ga|_gid)/,
            },
          ],
        },
      },
    },

    language: {
      default: defaultLang,
      translations: {
        en: getCookieConsentTranslations("en"),
        pl: getCookieConsentTranslations("pl"),
      },
    },
  };
};

const pluginConfig: CookieConsentConfig = createPluginConfig("pl");

export default pluginConfig;
export { createPluginConfig };
